import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type ComboboxSelectedItem,
    type ComboboxValue,
    Combobox,
    Avatar,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

type Member = {
    department: "engineering" | "design";
    email: string;
    name: string;
    id: string;
};

const NAME = "Combobox";

const USAGE_CODE = `const [assignee, setAssignee] = useState("");

<Combobox
    onValueChange={(value, items) => setAssignee(items[0]?.__title ?? "")}
    titleExtractor={(member) => member.name}
    keyExtractor={(member) => member.id}
    placeholder="Select a teammate"
    spreadBehaviour="stretch"
    title="Assignee"
    data={members}
/>`;

const CLEAR_CODE = `<Combobox
    defaultValue="ada"
    titleExtractor={(member) => member.name}
    keyExtractor={(member) => member.id}
    spreadBehaviour="stretch"
    isClearEnabled={true}
    title="Assignee"
    data={members}
/>`;

const REMOTE_CODE = `<Combobox
    onSearch={(text) => api.searchMembers(text)}
    titleExtractor={(member) => member.name}
    keyExtractor={(member) => member.id}
    title="Teammate"
    data={members}
/>`;

const MEMBERS: Array<Member> = [
    {
        department: "engineering",
        email: "ada@ncore.app",
        name: "Ada Lovelace",
        id: "ada"
    },
    {
        department: "engineering",
        email: "grace@ncore.app",
        name: "Grace Hopper",
        id: "grace"
    },
    {
        department: "engineering",
        email: "alan@ncore.app",
        name: "Alan Turing",
        id: "alan"
    },
    {
        email: "dieter@ncore.app",
        department: "design",
        name: "Dieter Rams",
        id: "dieter"
    },
    {
        email: "paula@ncore.app",
        department: "design",
        name: "Paula Scher",
        id: "paula"
    },
    {
        email: "susan@ncore.app",
        department: "design",
        name: "Susan Kare",
        id: "susan"
    }
];

const INITIAL_MEMBERS: Array<string> = [
    "ada",
    "grace"
];

const ADA_SELECTION = "ada";

const INITIAL_SKILLS = [
    "React Native",
    "TypeScript",
    "Swift"
];

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const MAX_TEAM = 4;

const getMemberName = (member: Member) => member.name;

const getMemberKey = (member: Member) => member.id;

const getMemberEmail = (member: Member) => member.email;

const getMemberIcon = (member: Member) => <Avatar
    title={member.name}
    size="xxSmall"
/>;

const STATUSES = [
    "pending",
    "shipped",
    "delivered"
];

const LABEL_CODE = `<Combobox
    CustomPrefixComponent={() => <Text variant="bodyMediumSize" color="mid">Status:</Text>}
    defaultValue={["pending"]}
    titleExtractor={(status) => status.title}
    keyExtractor={(status) => status.id}
    accessibilityLabel="Status"
    selectBehaviour="multiple"
    spreadBehaviour="free"
    data={statuses}
/>`;

const getText = (text: string) => text;

const getStatusKey = (status: string) => status;

const searchMembers = (text: string) => Promise.resolve(MEMBERS.filter((member) => member.name.toLowerCase().includes(text.toLowerCase())));

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("comboboxPage.multipleTitle"),
        id: "multiple"
    },
    {
        title: t("comboboxPage.groupsTitle"),
        id: "groups"
    },
    {
        title: t("comboboxPage.searchTitle"),
        id: "search"
    },
    {
        title: t("comboboxPage.clearTitle"),
        id: "clear"
    },
    {
        title: t("comboboxPage.labelTitle"),
        id: "label"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ComboboxPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        assignee,
        setAssignee
    ] = useState("");

    const [
        skills,
        setSkills
    ] = useState(INITIAL_SKILLS);

    const departments: Record<Member["department"], string> = {
        engineering: t("comboboxPage.departmentEngineering"),
        design: t("comboboxPage.departmentDesign")
    };

    const getMemberGroup = (member: Member) => departments[member.department];

    const getStatusTitle = (status: string) => t(`comboboxPage.status.${status}`);

    const renderStatusPrefix = () => <Text
        variant="bodyMediumSize"
        color="mid"
    >
        {t("comboboxPage.statusPrefix")}
    </Text>;

    const handleAssigneeChange = (_value: ComboboxValue, items: Array<ComboboxSelectedItem>) => {
        setAssignee(items[0]?.__title ?? "");
    };

    const handleCreateSkill = (text: string) => {
        setSkills((currentSkills) => [
            ...currentSkills,
            text
        ]);
    };

    return <Article>
        <ComponentHeader
            lead={t("comboboxPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("comboboxPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("comboboxPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("comboboxPage.overviewCaption")}
                isStacked={true}
            >
                <Combobox
                    hintText={assignee ? t("comboboxPage.assignedTo", [
                        assignee
                    ]) : undefined}
                    placeholder={t("comboboxPage.assigneePlaceholder")}
                    searchPlaceholder={t("comboboxPage.searchPeople")}
                    onValueChange={handleAssigneeChange}
                    title={t("comboboxPage.assignee")}
                    titleExtractor={getMemberName}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    data={MEMBERS}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("comboboxPage.multipleTitle")}
            id="multiple"
        >
            <Paragraph>
                {t("comboboxPage.multipleText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Combobox
                    searchPlaceholder={t("comboboxPage.searchPeople")}
                    hintText={t("comboboxPage.teamHint", [
                        MAX_TEAM
                    ])}
                    renderOptionIcon={getMemberIcon}
                    defaultValue={INITIAL_MEMBERS}
                    title={t("comboboxPage.team")}
                    titleExtractor={getMemberName}
                    keyExtractor={getMemberKey}
                    selectBehaviour="multiple"
                    spreadBehaviour="stretch"
                    maxChoice={MAX_TEAM}
                    data={MEMBERS}
                />
            </Preview>
        </Section>
        <Section
            title={t("comboboxPage.groupsTitle")}
            id="groups"
        >
            <Paragraph>
                {t("comboboxPage.groupsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Combobox
                    placeholder={t("comboboxPage.assigneePlaceholder")}
                    searchPlaceholder={t("comboboxPage.searchPeople")}
                    subTitleExtractor={getMemberEmail}
                    title={t("comboboxPage.reviewer")}
                    renderOptionIcon={getMemberIcon}
                    groupExtractor={getMemberGroup}
                    titleExtractor={getMemberName}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    data={MEMBERS}
                />
            </Preview>
        </Section>
        <Section
            title={t("comboboxPage.searchTitle")}
            id="search"
        >
            <Paragraph>
                {t("comboboxPage.searchText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Combobox
                    placeholder={t("comboboxPage.skillsPlaceholder")}
                    hintText={t("comboboxPage.skillsHint")}
                    title={t("comboboxPage.skills")}
                    onCreate={handleCreateSkill}
                    selectBehaviour="multiple"
                    spreadBehaviour="stretch"
                    titleExtractor={getText}
                    keyExtractor={getText}
                    data={skills}
                />
                <Combobox
                    searchPlaceholder={t("comboboxPage.searchPeople")}
                    hintText={t("comboboxPage.remoteHint")}
                    title={t("comboboxPage.teammate")}
                    titleExtractor={getMemberName}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    onSearch={searchMembers}
                    data={MEMBERS}
                />
            </Preview>
            <CodeBlock
                code={REMOTE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("comboboxPage.clearTitle")}
            id="clear"
        >
            <Paragraph>
                {t("comboboxPage.clearText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Combobox
                    placeholder={t("comboboxPage.assigneePlaceholder")}
                    searchPlaceholder={t("comboboxPage.searchPeople")}
                    title={t("comboboxPage.assignee")}
                    renderOptionIcon={getMemberIcon}
                    titleExtractor={getMemberName}
                    defaultValue={ADA_SELECTION}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    isClearEnabled={true}
                    data={MEMBERS}
                />
                <Combobox
                    searchPlaceholder={t("comboboxPage.searchPeople")}
                    renderOptionIcon={getMemberIcon}
                    defaultValue={INITIAL_MEMBERS}
                    title={t("comboboxPage.team")}
                    titleExtractor={getMemberName}
                    keyExtractor={getMemberKey}
                    selectBehaviour="multiple"
                    spreadBehaviour="stretch"
                    isClearEnabled={true}
                    data={MEMBERS}
                />
            </Preview>
            <CodeBlock
                code={CLEAR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("comboboxPage.labelTitle")}
            id="label"
        >
            <Paragraph>
                {t("comboboxPage.labelText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Combobox
                    accessibilityLabel={t("comboboxPage.statusLabel")}
                    CustomPrefixComponent={renderStatusPrefix}
                    titleExtractor={getStatusTitle}
                    keyExtractor={getStatusKey}
                    selectBehaviour="multiple"
                    spreadBehaviour="stretch"
                    defaultValue={[
                        "pending"
                    ]}
                    data={STATUSES}
                />
            </Preview>
            <CodeBlock
                code={LABEL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("comboboxPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <Combobox
                        titleExtractor={getMemberName}
                        defaultValue={ADA_SELECTION}
                        keyExtractor={getMemberKey}
                        spreadBehaviour="stretch"
                        data={MEMBERS}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("comboboxPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Combobox
                    title={t("comboboxPage.disabled")}
                    titleExtractor={getMemberName}
                    defaultValue={ADA_SELECTION}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    isDisabled={true}
                    data={MEMBERS}
                />
                <Combobox
                    title={t("comboboxPage.readOnly")}
                    titleExtractor={getMemberName}
                    defaultValue={ADA_SELECTION}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    isReadOnly={true}
                    data={MEMBERS}
                />
                <Combobox
                    title={t("comboboxPage.loading")}
                    titleExtractor={getMemberName}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    isLoading={true}
                    data={MEMBERS}
                />
                <Combobox
                    hintText={t("comboboxPage.hintDanger")}
                    titleExtractor={getMemberName}
                    keyExtractor={getMemberKey}
                    spreadBehaviour="stretch"
                    isRequired={true}
                    data={MEMBERS}
                    title="danger"
                    type="danger"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("comboboxPage.a11yTrigger"),
                    t("comboboxPage.a11yCombobox"),
                    t("comboboxPage.a11yLive"),
                    t("comboboxPage.a11yKeyboard"),
                    t("comboboxPage.a11yChips"),
                    t("comboboxPage.a11yTouch"),
                    t("comboboxPage.a11yStates")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("comboboxPage.propData"),
                        type: "Array<T>",
                        name: "data"
                    },
                    {
                        description: t("comboboxPage.propTitleExtractor"),
                        type: "(item: T, index: number) => string",
                        name: "titleExtractor"
                    },
                    {
                        description: t("comboboxPage.propKeyExtractor"),
                        type: "(item: T, index: number) => string",
                        name: "keyExtractor"
                    },
                    {
                        description: t("comboboxPage.propSubTitleExtractor"),
                        type: "(item: T) => string",
                        name: "subTitleExtractor"
                    },
                    {
                        description: t("comboboxPage.propGroupExtractor"),
                        type: "(item: T) => string",
                        name: "groupExtractor"
                    },
                    {
                        description: t("comboboxPage.propRenderOptionIcon"),
                        type: "(item: T) => ReactElement",
                        name: "renderOptionIcon"
                    },
                    {
                        description: t("comboboxPage.propSelectBehaviour"),
                        type: "\"single\" | \"multiple\"",
                        defaultValue: "\"single\"",
                        name: "selectBehaviour"
                    },
                    {
                        description: t("comboboxPage.propValue"),
                        type: "string | Array<string>",
                        name: "value"
                    },
                    {
                        description: t("comboboxPage.propDefaultValue"),
                        type: "string | Array<string>",
                        name: "defaultValue"
                    },
                    {
                        type: "(value: string | Array<string>, selectedItems: Array<{ __title: string; __key: string }>) => void",
                        description: t("comboboxPage.propOnValueChange"),
                        name: "onValueChange"
                    },
                    {
                        description: t("comboboxPage.propOnSearch"),
                        type: "(text: string) => Promise<Array<T>>",
                        name: "onSearch"
                    },
                    {
                        description: t("comboboxPage.propOnCreate"),
                        type: "(text: string) => void",
                        name: "onCreate"
                    },
                    {
                        description: t("comboboxPage.propMaxChoice"),
                        name: "maxChoice",
                        type: "number"
                    },
                    {
                        description: t("comboboxPage.propMinChoice"),
                        name: "minChoice",
                        type: "number"
                    },
                    {
                        description: t("comboboxPage.propIsClearEnabled"),
                        name: "isClearEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("comboboxPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("comboboxPage.propSearchPlaceholder"),
                        name: "searchPlaceholder",
                        type: "string"
                    },
                    {
                        description: t("comboboxPage.propEmptyText"),
                        name: "emptyText",
                        type: "string"
                    },
                    {
                        description: t("comboboxPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("comboboxPage.propCustomPrefixComponent"),
                        name: "CustomPrefixComponent",
                        type: "() => ReactNode"
                    },
                    {
                        description: t("comboboxPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("comboboxPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("comboboxPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("comboboxPage.propSize"),
                        name: "size"
                    },
                    {
                        description: t("comboboxPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("comboboxPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("comboboxPage.propIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("comboboxPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("comboboxPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("comboboxPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("comboboxPage.propIsShowHintTextIcon"),
                        name: "isShowHintTextIcon",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("comboboxPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("comboboxPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("comboboxPage.propsNote")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("comboboxPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ComboboxPage;
